import { Image as ImageIcon, Upload, X } from 'lucide-react';
import { useEffect, useId, useMemo } from 'react';
import { useTranslation } from '@dukanti/shared';
import type { TranslationKey } from '@dukanti/shared';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface CategoryMediaPickerProps {
  readonly file: File | null;
  readonly existingUrl?: string | null;
  readonly errorKey?: TranslationKey | null;
  readonly disabled?: boolean;
  readonly onFileChange: (file: File | null) => void;
  readonly onRemoveExisting?: () => void;
}

export function CategoryMediaPicker({
  file,
  existingUrl,
  errorKey,
  disabled = false,
  onFileChange,
  onRemoveExisting,
}: CategoryMediaPickerProps) {
  const { t } = useTranslation();
  const inputId = useId();
  const previewUrl = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  const visibleUrl = previewUrl ?? existingUrl ?? null;

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  function handleChange(event: React.ChangeEvent<HTMLInputElement>): void {
    const next = event.currentTarget.files?.[0] ?? null;
    onFileChange(next);
    event.currentTarget.value = '';
  }

  function handleRemove(): void {
    if (file) {
      onFileChange(null);
      return;
    }
    onRemoveExisting?.();
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <div
          className={cn(
            'border-border bg-admin-surface-muted flex size-20 shrink-0 items-center justify-center overflow-hidden rounded-lg border',
            errorKey && 'border-destructive',
          )}
        >
          {visibleUrl ? (
            <img
              src={visibleUrl}
              alt={t('admin.categories.form.mediaPreviewAlt')}
              className="h-full w-full object-cover"
            />
          ) : (
            <ImageIcon className="text-muted-foreground size-8" strokeWidth={1.5} aria-hidden />
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" variant="outline" disabled={disabled} asChild>
            <label htmlFor={inputId} className="cursor-pointer">
              <Upload className="size-4" aria-hidden />
              <span>
                {t(
                  visibleUrl
                    ? 'admin.categories.form.mediaReplace'
                    : 'admin.categories.form.mediaUpload',
                )}
              </span>
            </label>
          </Button>
          {visibleUrl ? (
            <Button type="button" variant="ghost" disabled={disabled} onClick={handleRemove}>
              <X className="size-4" aria-hidden />
              <span>{t('admin.categories.form.mediaRemove')}</span>
            </Button>
          ) : null}
        </div>
      </div>
      <input
        id={inputId}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="sr-only"
        disabled={disabled}
        onChange={handleChange}
        aria-describedby={`${inputId}-hint${errorKey ? ` ${inputId}-error` : ''}`}
      />
      <p id={`${inputId}-hint`} className="text-muted-foreground text-start text-xs">
        {t('admin.categories.form.mediaHint')}
      </p>
      {errorKey ? (
        <p id={`${inputId}-error`} role="alert" className="text-destructive text-start text-xs">
          {t(errorKey)}
        </p>
      ) : null}
    </div>
  );
}
